import { createState } from "state-pool"

//The current environment of the Blind Insight application
export const CurrentEnvironment = createState("")

// The current user object if we have one
export const CurrentUser = createState({})

// Hash of organization.id to the organization object
export const Organizations = createState({})
export const CurrentOrgId = createState("")
export const CurrentOrg = createState(null)

// Hash of dataset.id to the dataset object
export const Datasets = createState({})

// Hash of schema.id to the schema
export const Schemas = createState({})

// Hash of schema.id to the number of records in that schema
export const SchemaRecordCounts = createState({})

// Hash of dataset.id to a list of schema.id
export const DatasetSchemas = createState({})

// Hash of the schema.id to the schema with hashed field keys
export const HashedSchemas = createState({})

// Hash of schema.id to the boolean result of recordsCanDecrypt for that
// schema. Cached at the schema level (not per-record) because permission
// is granted at the schema level, and the WASM round-trip through chrome
// .storage is expensive (~80–150 ms per call). Survives component
// remounts so a route shuffle or parallel mount cascade doesn't re-issue
// the check. Cleared on logout via the existing state-pool reset path.
export const CanDecryptBySchema = createState({})

// The current status of the upload job
export const JobStatus = createState(null)

// The current name of the file being uploaded
export const FileName = createState("")

// The current File specific error
export const FileError = createState(null)

// The current API error
export const ApiError = createState(null)

// The current upload progress
export const UploadProgress = createState(0)

// Upload status enum
export const UploadStatus = {
  NONE: "NONE",
  UPLOADING: "UPLOADING",
  TERMINATED: "TERMINATED",
  COMPLETED: "COMPLETED",
}

// Whether or not an upload is in progress
export const IsUploading = createState(UploadStatus.NONE)

// Track success/failure of each batch in the upload
export const UploadBatchStatus = createState(null)

// Whether a delete operation is currently in flight (disables nav/actions)
export const IsDeleting = createState(false)

// Track the current alert index
export const CurrentAlertIndex = createState(0)

// List of all teams and members for a schema
export const SchemaTeams = createState([])

// Object to use as a default import
export function Store(name) {
  const stores = {
    currentUser: CurrentUser,
    organizations: Organizations,
    datasets: Datasets,
    schemas: Schemas,
    datasetSchemas: DatasetSchemas,
    hashedSchemas: HashedSchemas,
    jobStatus: JobStatus,
    fileName: FileName,
    fileError: FileError,
    uploadProgress: UploadProgress,
    isUploading: IsUploading,
    apiError: ApiError,
    uploadBatchStatus: UploadBatchStatus,
  }
  if (!stores[name]) throw new Error(`${name} is not a valid store`)
  return stores[name]
}

// The current limit. Default is 100 because server-side search cost is
// roughly fixed regardless of page size on high-cardinality queries
// (benchmarked 2026-05-10: 25→5.19s, 50→3.44s, 100→3.43s median). The
// DataTable page-size selector still offers smaller values for callers
// who want them.
export const Limit = createState(100)

// The current offset
export const Offset = createState(0)

// List of active filters
export const CurrentFilters = createState([])

export const Pagination = createState({
  limit: Limit.value,
  offset: Offset.value,
})

// List of alerts to display to the user
export const Alerts = createState([])

// List of alerts that the user has dismissed this browser session
export const DontShowAgainList = createState([])

// Cross-page LRU cache for decrypted records, keyed by record.id.
// Lives outside React state so reads and writes don't trigger re-renders;
// callers fold cache hits into local state explicitly. Eviction is
// least-recently-used, capped at DECRYPTED_CACHE_MAX entries, biased small
// until we measure memory on real customer datasets.
const DECRYPTED_CACHE_MAX = 5000
const _decryptedCache = new Map()

export function cacheGetDecrypted(id) {
  if (!_decryptedCache.has(id)) return undefined
  // Bump to most-recently-used by re-inserting (Map preserves insertion order)
  const value = _decryptedCache.get(id)
  _decryptedCache.delete(id)
  _decryptedCache.set(id, value)
  return value
}

export function cacheSetDecrypted(entries) {
  if (!entries) return
  for (const id in entries) {
    if (_decryptedCache.has(id)) _decryptedCache.delete(id)
    _decryptedCache.set(id, entries[id])
  }
  while (_decryptedCache.size > DECRYPTED_CACHE_MAX) {
    const oldestKey = _decryptedCache.keys().next().value
    _decryptedCache.delete(oldestKey)
  }
}

export function cacheClearDecrypted() {
  _decryptedCache.clear()
}

// Test/introspection only.
export function _cacheSizeDecrypted() {
  return _decryptedCache.size
}

export const DECRYPTED_CACHE_MAX_SIZE = DECRYPTED_CACHE_MAX

export default Store
